/*  MUI  */
import {
    AddCircleOutlineRounded,
    FilterAlt,
    Groups,
    ThumbUpOutlined
} from '@mui/icons-material';
import {
    Badge,
    Box,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    MenuItem,
    Select,
    TableCell,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

/*  Shared  */
import { DateFormat } from 'application/Shared/Components/Core';
import { ManagedTable } from 'application/Shared/Components/Table';
import {
    FontSizes,
    NoteTypesReversed,
    NoteTypesValues
} from 'application/Shared/Constants';
import { Helpers, Notify } from 'application/Shared/Helpers';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

const headers = [];

/*  State  */
const state = () => ({
    loaded: false,
    notes: [],
    type: 'All',
    visible: []
});

class Community extends Component {
    constructor(props) {
        super(props);
        this.state = state();
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        const { notes } = this.props;
        this.setState({ loaded: true, notes, visible: notes });
    };

    handleImport = (row) => {
        const { onImport } = this.props;
        const { type } = this.state;
        let { notes } = this.state;
        const { id } = row;

        if (onImport) {
            onImport({ row });
        }

        notes = notes.filter((n) => n.id !== id);
        this.setState({ notes }, () => {
            this.handleFilter(type);
        });
    };

    handleFilter = (type) => {
        const { notes } = this.state;
        let visible = notes;
        if (type !== 'All') {
            visible = notes.filter((n) => n.type === NoteTypesReversed[type]);
        }
        this.setState({ type, visible });
    };

    handleLike = (params) => {
        const { id } = params;
        const { type } = this.state;
        let { notes } = this.state;

        notes = notes.map((n) => {
            if (n.id === id) {
                return { ...n, liked: true };
            }
            return n;
        });

        Notify.i('Thank you for the Community feedback!');

        this.setState({ notes }, () => {
            this.handleFilter(type);
        });
    };

    renderRow = (row) => {
        const { id, type, message, liked, dateCreated } = row;
        const StyledBadge = styled(Badge)({
            badge: {
                height: '30px',
                width: '30px'
            }
        });

        return (
            <TableCell style={{ border: 'none', margin: 0, padding: '8px' }}>
                <Box>
                    <small>
                        <strong style={{ marginLeft: '35px' }}>{type}</strong>
                        <Box
                            sx={{
                                float: 'right',
                                marginTop: '16px'
                            }}
                        >
                            <DateFormat date={dateCreated} />
                        </Box>
                    </small>
                </Box>
                <Grid
                    container
                    spacing={3}
                    style={{ alignItems: 'center', marginBottom: '8px' }}
                >
                    <Grid item xs={2}>
                        <IconButton
                            data-trackid="application_community_import"
                            size="small"
                            onClick={() => this.handleImport(row)}
                            sx={{ color: 'button.primary' }}
                        >
                            <AddCircleOutlineRounded />
                        </IconButton>
                    </Grid>
                    <Grid item xs={9}>
                        <StyledBadge
                            variant="liked" // So that does not grab the standard styles.
                            anchorOrigin={{
                                horizontal: 'right',
                                vertical: 'bottom'
                            }}
                            badgeContent={
                                <IconButton
                                    data-trackid="application_community_like"
                                    size="small"
                                    variant="liked"
                                    onClick={() => this.handleLike({ id })}
                                    sx={{
                                        '&.MuiIconButton-root': {
                                            backgroundColor: liked
                                                ? 'community.likeBadge.likedBackgroundColor'
                                                : 'community.likeBadge.unlikedBackgroundColor',
                                            height: '24px',
                                            width: '24px'
                                        },
                                        '&:hover': {
                                            backgroundColor: liked
                                                ? 'community.likeBadge.likedBackgroundColor'
                                                : 'community.likeBadge.unlikedBackgroundColor'
                                        },
                                        border: '1px solid',
                                        borderColor:
                                            'community.likeBadge.borderColor'
                                    }}
                                >
                                    <ThumbUpOutlined
                                        sx={{
                                            color: liked
                                                ? 'community.likeBadge.unlikedBackgroundColor'
                                                : 'community.likeBadge.likedBackgroundColor',
                                            fontSize: FontSizes[0]
                                        }}
                                    />
                                </IconButton>
                            }
                            sx={{
                                border: '1px solid',
                                borderColor: 'border.primary',
                                borderRadius: '16px',
                                padding: '10px',
                                width: '100%'
                            }}
                            overlap="rectangular"
                        >
                            {message}
                        </StyledBadge>
                    </Grid>
                </Grid>
            </TableCell>
        );
    };

    renderFilter = () => {
        const { t } = this.props;
        const { type } = this.state;

        return (
            <FormControl variant="outlined" fullWidth size="small">
                <Select
                    value={type}
                    onChange={(e) => this.handleFilter(e.target.value)}
                    IconComponent={FilterAlt}
                >
                    <MenuItem value="All">All</MenuItem>
                    {NoteTypesValues.map((item) => (
                        <MenuItem key={item} value={item}>
                            {t(`Notes.${item}`)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    renderTable = () => {
        const { open } = this.props;
        const { loaded, visible } = this.state;

        if (!open || !loaded) {
            return null;
        }

        if (!Helpers.count(visible)) {
            return (
                <span style={{ padding: 20, position: 'absolute' }}>
                    No Results
                </span>
            );
        }

        return (
            <ManagedTable
                headers={headers}
                noEmptyRows
                renderBody={this.renderRow}
                renderBodyKey="id"
                rows={visible}
                rowsPerPage={-1}
                sortDirection="DESC"
                sortFieldType="date"
                sortKey="dateCreated"
            />
        );
    };

    render() {
        const { t, open, application } = this.props;

        return (
            <Drawer
                variant="permanent"
                open={open}
                anchor="right"
                PaperProps={{
                    sx: {
                        borderRadius: 1,
                        boxShadow: 24,
                        maxHeight: 'calc(100% - 200px);',
                        maxWidth: '23%',
                        minWidth: '23%',
                        right: 20,
                        top: 100
                    }
                }}
            >
                <Typography
                    variant="h4"
                    style={{ fontWeight: 400, marginBottom: '8px' }}
                >
                    <Box display="flex" alignItems="center">
                        <Groups
                            color="secondary"
                            style={{ marginRight: '8px' }}
                        />
                        {t('Common.community')}
                    </Box>
                </Typography>
                <Typography paragraph>
                    Curated tips from <strong>{application.name}</strong>{' '}
                    experts.
                </Typography>
                <Box mb="4px">{this.renderFilter()}</Box>
                <Box mt="8px">{this.renderTable()}</Box>
            </Drawer>
        );
    }
}

export default withTranslation()(Community);
